import { useCallback, useState } from 'react'
import { useSessionStorage } from 'react-use'
import { gql, useLazyQuery } from '@apollo/client'
import type { GlobalSearchResponse } from '@ztechapps/common.contract'
import { useDeepCompareEffect } from '@ztechapps/common.hook'

const STORAGE_KEY = 'global-search-query'

export type GlobalSearchVariables = {
  input: {
    filter: {
      query: string
    }
    options?: Record<string, any>
  }
}

export type GlobalSearchData = {
  globalSearch: GlobalSearchResponse
}

const GLOBAL_SEARCH_QUERY = gql`
  query GlobalSearch($input: GlobalSearchInput!) {
    globalSearch(input: $input) {
      groups {
        module
        count
        results {
          _id
          code
          name
          summary
          module
          score
        }
      }
      totalResults
      executionTimeMs
    }
  }
`

export type UseGlobalSearchResult = {
  searchQuery: string
  setSearchQuery: (query: string) => void
  handleSearch: () => void
  results: GlobalSearchResponse | undefined
  loading: boolean
  error: any
  canSearch: boolean
  clearSearch: () => void
}

export const useGlobalSearch = (): UseGlobalSearchResult => {
  const [searchQuery, setSearchQuery] = useSessionStorage<string>(STORAGE_KEY, '')

  const [executeSearch, { data, loading, error }] = useLazyQuery<GlobalSearchData, GlobalSearchVariables>(
    GLOBAL_SEARCH_QUERY
  )

  const [results, setResults] = useState<GlobalSearchResponse | undefined>(data?.globalSearch)

  useDeepCompareEffect(() => {
    setResults(data?.globalSearch)
  }, [data?.globalSearch])

  const handleSearch = useCallback(() => {
    if (searchQuery && searchQuery.length >= 3) {
      executeSearch({
        variables: {
          input: {
            filter: {
              query: searchQuery,
            },
            options: {},
          },
        },
      })
    } else {
      //clear the data
      setSearchQuery('')
      setResults(undefined)
    }
  }, [searchQuery, executeSearch])

  const clearSearch = useCallback(() => {
    setSearchQuery('')
  }, [])

  return {
    searchQuery: searchQuery || '',
    setSearchQuery,
    handleSearch,
    results, //searchQuery && searchQuery.length >= 3 ? data?.globalSearch : undefined,
    loading,
    error,
    canSearch: (searchQuery?.length ?? 0) >= 3,
    clearSearch,
  }
}
